import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Clients.css';

// ── Icons ────────────────────────────────────────────────────────────────────

const IconChevronLeft = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconChevronRight = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ── Industry data ─────────────────────────────────────────────────────────────

interface Industry {
  id: string;
  name: string;
  tagline: string;
  image: string;
  imageAlt: string;
  applications: string[];
  accent: string;
}

const industries: Industry[] = [
  {
    id: 'alimentaria',
    name: 'Industria Alimentaria',
    tagline: 'Dosificación precisa y control de calidad en cada etapa del proceso productivo.',
    image: '/images/ingredient-dosing.jpg',
    imageAlt: 'Planta industrial de procesamiento de alimentos a gran escala',
    applications: [
      'Dosificación automática de ingredientes',
      'Control de peso en líneas de envasado',
      'Verificación de porciones y gramaje',
      'Trazabilidad por lote para auditorías',
    ],
    accent: '#38BDF8',
  },
  {
    id: 'farmaceutica',
    name: 'Industria Farmacéutica',
    tagline: 'Precisión metrológica y cumplimiento normativo para procesos críticos.',
    image: 'images/customers-pharma.avif',
    imageAlt: 'Laboratorio farmacéutico con equipos de medición',
    applications: [
      'Pesaje de principios activos y excipientes',
      'Calibración certificada ante EMA',
      'Cumplimiento BPM y normativas sanitarias',
      'Balanzas de alta precisión analítica',
    ],
    accent: '#34D399',
  },
  {
    id: 'quimica',
    name: 'Industria Química',
    tagline: 'Equipos robustos para entornos exigentes con materiales corrosivos y de alto riesgo.',
    image: 'images/customers-chemicals.avif',
    imageAlt: 'Planta química industrial con tanques y tuberías',
    applications: [
      'Pesaje de materias primas a granel',
      'Control de mezclas y formulaciones',
      'Básculas con protección IP para zonas húmedas',
      'Integración con sistemas SCADA',
    ],
    accent: '#F59E0B',
  },
  {
    id: 'logistica',
    name: 'Logística y Transporte',
    tagline: 'Control de peso en toda la cadena de suministro, desde el almacén hasta la entrega.',
    image: 'images/customers-transportation.avif',
    imageAlt: 'Centro de distribución logística con montacargas',
    applications: [
      'Básculas camioneras para control de acceso',
      'Pesaje con montacargas en almacén',
      'Verificación de embarques y paquetería',
      'Reportes de peso para facturación y fletes',
    ],
    accent: '#818CF8',
  },
  {
    id: 'agroindustria',
    name: 'Agroindustria',
    tagline: 'Soluciones de pesaje para el campo, el silo y la planta de procesamiento agrícola.',
    image: 'images/customers-agro.avif',
    imageAlt: 'Campo agrícola y silos de almacenamiento de granos',
    applications: [
      'Básculas para báscula de silos y tolvas',
      'Control de cosecha y rendimiento por parcela',
      'Pesaje de ganado en pie',
      'Gestión de entradas y salidas en centros de acopio',
    ],
    accent: '#86EFAC',
  },
  {
    id: 'construccion',
    name: 'Construcción y Minería',
    tagline: 'Equipos de alta capacidad para el pesaje de materiales a granel y vehículos pesados.',
    image: 'images/customers-construction.avif',
    imageAlt: 'Sitio de construcción con maquinaria pesada',
    applications: [
      'Báscula camionera de gran tonelaje',
      'Control de cemento, grava y materiales a granel',
      'Pesaje dinámico de camiones en movimiento',
      'Sistemas "WEIGH LINE" ferroviarios',
    ],
    accent: '#FCA5A5',
  },
  {
    id: 'manufactura',
    name: 'Manufactura Industrial',
    tagline: 'Automatización del pesaje integrada directamente en sus líneas de producción.',
    image: '/images/customers-manufacture.jpg',
    imageAlt: 'Banda transportadora con báscula verificadora en línea de producción industrial',
    applications: [
      'Checkweighers en línea de producción',
      'Básculas de banda para flujo continuo',
      'Integración con ERP y sistemas MES',
      'Rechazo automático de productos no conformes',
    ],
    accent: '#67E8F9',
  },
];

// ── Slider component ──────────────────────────────────────────────────────────

const IndustrySlider: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [animating, setAnimating] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const total = industries.length;

  const goTo = useCallback((index: number) => {
    if (animating) return;
    setAnimating(true);
    setCurrent((index + total) % total);
    setTimeout(() => setAnimating(false), 600);
  }, [animating, total]);

  const goNext = useCallback(() => goTo(current + 1), [goTo, current]);
  const goPrev = useCallback(() => goTo(current - 1), [goTo, current]);

  // Auto-advance
  useEffect(() => {
    if (isHovered || isPaused) return;
    intervalRef.current = setInterval(goNext, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isHovered, isPaused, goNext]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (document.activeElement === sliderRef.current || sliderRef.current?.contains(document.activeElement)) {
        if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); goPrev(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  const industry = industries[current];

  return (
    <div
      ref={sliderRef}
      className="industry-slider"
      role="region"
      aria-label="Sectores industriales"
      tabIndex={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      {/* Preload all images silently */}
      <div className="slider-preload" aria-hidden="true">
        {industries.map((ind) => (
          <img key={ind.id} src={ind.image} alt="" loading="eager" />
        ))}
      </div>

      {/* Slide */}
      <div
        className={`slider-slide${animating ? ' slider-slide--animating' : ''}`}
        style={{ backgroundImage: `url(${industry.image})` }}
        role="img"
        aria-label={industry.imageAlt}
      >
        {/* Gradient overlay */}
        <div className="slider-overlay" />

        {/* Content */}
        <div className="slider-content container">
          <div className="slider-inner">
            <span className="slider-eyebrow" style={{ color: industry.accent }}>
              {String(current + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
            <h2 className="slider-title">{industry.name}</h2>
            <p className="slider-tagline">{industry.tagline}</p>
            <ul className="slider-bullets">
              {industry.applications.map((app) => (
                <li key={app}>
                  <span className="slider-bullet-icon" style={{ color: industry.accent }}>
                    <IconCheck />
                  </span>
                  {app}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Prev / Next */}
        <button
          className="slider-arrow slider-arrow--prev"
          onClick={goPrev}
          aria-label="Sector anterior"
        >
          <IconChevronLeft />
        </button>
        <button
          className="slider-arrow slider-arrow--next"
          onClick={goNext}
          aria-label="Sector siguiente"
        >
          <IconChevronRight />
        </button>

        {/* Progress bar */}
        <div className="slider-progress" aria-hidden="true">
          <div
            className={`slider-progress-bar${isHovered ? ' slider-progress-bar--paused' : ''}`}
            style={{ '--accent': industry.accent } as React.CSSProperties}
          />
        </div>
      </div>

      {/* Dot navigation */}
      <div className="slider-dots" role="tablist" aria-label="Seleccionar sector">
        {industries.map((ind, i) => (
          <button
            key={ind.id}
            role="tab"
            aria-selected={i === current}
            aria-label={ind.name}
            className={`slider-dot${i === current ? ' slider-dot--active' : ''}`}
            onClick={() => goTo(i)}
          />
        ))}
      </div>

      {/* Industry thumbnails strip */}
      <div className="slider-thumbs" aria-hidden="true">
        {industries.map((ind, i) => (
          <button
            key={ind.id}
            className={`slider-thumb${i === current ? ' slider-thumb--active' : ''}`}
            onClick={() => goTo(i)}
            tabIndex={-1}
          >
            <img src={ind.image} alt={ind.name} loading="lazy" />
            <span className="slider-thumb-label">{ind.name.replace('Industria ', '').replace(' Industrial', '').replace(' y Transporte', '').replace(' y Minería', '')}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const Clients: React.FC = () => {
  return (
    <div className="clients">

      {/* Hero */}
      <section className="clients-hero">
        <div className="container">
          <div className="clients-hero-eyebrow">Nuestros Mercados</div>
          <h1>Industrias que Atendemos</h1>
          <p>
            Más de 30 años integrando soluciones de pesaje y automatización en los
            sectores más exigentes de la industria mexicana.
          </p>
        </div>
      </section>

      {/* Slider */}
      <section className="clients-slider-section">
        <IndustrySlider />
      </section>

      {/* Stats strip */}
      <section className="clients-stats">
        <div className="container">
          <div className="clients-stats-grid">
            <div className="clients-stat">
              <span className="clients-stat-number">7+</span>
              <span className="clients-stat-label">Sectores industriales</span>
            </div>
            <div className="clients-stat">
              <span className="clients-stat-number">+30</span>
              <span className="clients-stat-label">Años de experiencia</span>
            </div>
            <div className="clients-stat">
              <span className="clients-stat-number">Nacional</span>
              <span className="clients-stat-label">Cobertura de servicio</span>
            </div>
            <div className="clients-stat">
              <span className="clients-stat-number">3</span>
              <span className="clients-stat-label">Marcas líderes mundiales</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="clients-why">
        <div className="container">
          <div className="clients-why-header">
            <h2>¿Por qué empresas líderes nos eligen?</h2>
            <p>
              La combinación de equipo de primer nivel, técnicos certificados y soporte
              continuo nos convierte en el aliado estratégico de pesaje en México.
            </p>
          </div>
          <div className="clients-why-grid">
            <div className="clients-why-card">
              <div className="clients-why-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
              <h3>Calibración certificada EMA</h3>
              <p>Laboratorio acreditado M70 para calibración y verificación metrológica con trazabilidad a patrones nacionales.</p>
            </div>
            <div className="clients-why-card">
              <div className="clients-why-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </div>
              <h3>Sistemas a la medida</h3>
              <p>Diseñamos e integramos soluciones adaptadas al proceso específico de cada cliente, no soluciones genéricas de catálogo.</p>
            </div>
            <div className="clients-why-card">
              <div className="clients-why-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
                  <line x1="9" y1="3" x2="9" y2="18"/>
                  <line x1="15" y1="6" x2="15" y2="21"/>
                </svg>
              </div>
              <h3>Cobertura en toda la República</h3>
              <p>Técnicos propios en Guadalajara, CDMX, Monterrey y San Luis Potosí para atención en sitio sin demoras.</p>
            </div>
            <div className="clients-why-card">
              <div className="clients-why-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <path d="M8 21h8M12 17v4"/>
                </svg>
              </div>
              <h3>Integración con ERP y software</h3>
              <p>Conectamos el pesaje directamente con sus sistemas de gestión empresarial para trazabilidad y reportes automáticos.</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Clients;
